// src/router/index.js
// ✅ 2026.01.31 - Unhead와 충돌하는 메타태그 직접 조작 제거
import { createRouter, createWebHistory } from 'vue-router';

import { useAuthRoles } from '@/composables/useAuthRoles';
import { useMainStore } from '@/store/mainStore';
import userUtils from '@/common/userUtils';
import { logError, logWarn, logInfo, logDebug } from '@/utils/logHelper';

import {
  routes,
  ROLES,
  DEFAULT_HOME_ROUTE,
  GUEST_ONLY_PAGES,
} from './routes';

export { ROLES, DEFAULT_HOME_ROUTE };

// ============================================================================
// 라우터 인스턴스 생성
// ============================================================================

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: 'on',
  linkExactActiveClass: '',
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  },
});

// ============================================================================
// 가드 헬퍼 함수
// ============================================================================

async function tryRecoverAuth(mainStore) {
  if (mainStore.isLogin) return true;

  const token = userUtils.getAccessToken();
  if (!token) return false;

  try {
    await mainStore.checkAuthAction();
    logInfo('[RouterGuard] Auth recovered successfully');
    return mainStore.isLogin;
  } catch (error) {
    logWarn('[RouterGuard] Auth recovery failed:', error?.message || error);
    return false;
  }
}

function checkAuth(to, from, mainStore, authRoles) {
  if (!to.meta.requiresAuth) return null;

  if (!mainStore.isLogin) {
    logInfo('[RouterGuard] Auth required, show login modal only:', to.path);
    authRoles.ensureUserLoggedIn({ openModal: true });
    return { cancel: true };
  }

  return null;
}

function checkGuestOnly(to, mainStore) {
  if (!to.meta.guestOnly && !GUEST_ONLY_PAGES.has(to.name)) return null;

  if (mainStore.isLogin) {
    logInfo('[RouterGuard] Guest only page, redirecting to home:', to.path);
    return { name: DEFAULT_HOME_ROUTE };
  }

  return null;
}

function checkRoles(to, authRoles) {
  const requiredRoles = to.meta.roles;

  if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) {
    return null;
  }

  const hasPermission = authRoles.hasAnyRole(requiredRoles);

  if (!hasPermission) {
    const userRoles = authRoles.roles.value;
    logWarn('[RouterGuard] Access forbidden:', {
      path: to.path,
      requiredRoles,
      userRoles,
    });
    return { name: 'forbidden' };
  }

  return null;
}

function checkAdmin(to, authRoles) {
  if (!to.meta.requiresAdmin) return null;

  if (!authRoles.isAdmin()) {
    logWarn('[RouterGuard] Admin required:', to.path);
    return { name: DEFAULT_HOME_ROUTE };
  }

  return null;
}

// ============================================================================
// 전역 네비게이션 가드
// ============================================================================

router.beforeEach(async (to, from) => {
  let mainStore;
  let authRoles;

  try {
    mainStore = useMainStore();
    authRoles = useAuthRoles();
  } catch (error) {
    logWarn('[RouterGuard] Store not ready yet');
    return true;
  }

  await tryRecoverAuth(mainStore);

  const guestRedirect = checkGuestOnly(to, mainStore);
  if (guestRedirect) return guestRedirect;

  const authResult = checkAuth(to, from, mainStore, authRoles);
  if (authResult?.cancel) {
    return false;
  }
  if (authResult) return authResult;

  const roleRedirect = checkRoles(to, authRoles);
  if (roleRedirect) return roleRedirect;

  const adminRedirect = checkAdmin(to, authRoles);
  if (adminRedirect) return adminRedirect;

  return true;
});

// ============================================================================
// 라우트 변경 후 처리
// ✅ 개선: Unhead가 메타태그를 관리하므로 직접 조작 제거
// ============================================================================

router.afterEach((to, from) => {
  // ✅ 개발 환경 로깅만 유지 (SEO 메타태그는 Unhead가 처리)
  if (process.env.NODE_ENV === 'development') {
    logDebug('[Router] Navigation:', {
      from: from.path,
      to: to.path,
      name: to.name,
      meta: {
        title: to.meta?.title,
        noIndex: to.meta?.noIndex,
      },
    });
  }

  // ✅ 참고: 아래 기능들은 이제 Unhead(App.vue)에서 처리됨
  // - document.title 설정 → useHead()
  // - meta description → useHead()
  // - canonical URL → useHead()
  // - og:* 태그 → useHead()
  // - twitter:* 태그 → useHead()
});

// ============================================================================
// 라우터 에러 핸들링
// ============================================================================

router.onError((error, to, from) => {
  logError('[RouterError]', {
    message: error?.message || error,
    to: to?.path,
    from: from?.path,
  });

  if (
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Loading chunk') ||
    error?.message?.includes('Loading CSS chunk')
  ) {
    logWarn('[Router] Chunk loading failed, reloading page...');
    window.location.reload();
  }
});

export default router;